import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, BookOpen, Layers, FileWarning, TrendingUp, Award } from "lucide-react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";

const CHART = ["#726AF0", "#E64AA1", "#FFA81B", "#16A34A", "#A35FF6"];

function StatCard({ icon: Icon, label, value, accent = "from-[#726AF0] to-[#5B5BF0]", testid }) {
    return (
        <div className="mx-card p-5" data-testid={testid}>
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
                    <div className="font-display text-3xl mt-2">{value}</div>
                </div>
                <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center text-white shadow-mx-md`}>
                    <Icon className="h-5 w-5" />
                </div>
            </div>
        </div>
    );
}

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/dashboard/admin").then((r) => setData(r.data)).catch(() => {}); }, []);
    if (!data) return <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">{Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>;
    const s = data.stats;

    return (
        <div className="space-y-8" data-testid="admin-dashboard">
            <div>
                <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Welcome, Admin</div>
                <h1 className="font-display text-3xl lg:text-4xl mt-1">Platform overview</h1>
            </div>

            <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                <StatCard icon={Users} label="Total Students" value={s.total_students} testid="stat-total-students" />
                <StatCard icon={Users} label="Active Students" value={s.active_students} accent="from-[#16A34A] to-[#0EA458]" testid="stat-active-students" />
                <StatCard icon={BookOpen} label="Courses" value={s.total_courses} accent="from-[#E64AA1] to-[#EE46A3]" testid="stat-courses" />
                <StatCard icon={Layers} label="Batches" value={s.total_batches} accent="from-[#FFA81B] to-[#E47A00]" testid="stat-batches" />
                <StatCard icon={FileWarning} label="Pending Submissions" value={s.pending_submissions} accent="from-[#E11D48] to-[#FF4D6D]" testid="stat-pending" />
                <StatCard icon={TrendingUp} label="Avg Quiz Score" value={`${s.avg_quiz_score}%`} testid="stat-avg-quiz" />
                <StatCard icon={Award} label="Quiz Pass Rate" value={`${s.quiz_pass_rate}%`} accent="from-[#A35FF6] to-[#726AF0]" testid="stat-quiz-pass" />
                <StatCard icon={FileWarning} label="Graded Submissions" value={s.graded_submissions} accent="from-[#16A34A] to-[#0EA458]" testid="stat-graded" />
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
                <Card className="lg:col-span-2 mx-card">
                    <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Enrollments over time</CardTitle></CardHeader>
                    <CardContent className="h-72">
                        <ResponsiveContainer>
                            <LineChart data={data.enrollment_series}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                <XAxis dataKey="week" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                                <Line type="monotone" dataKey="count" stroke="#726AF0" strokeWidth={3} dot={{ r: 4, fill: "#E64AA1" }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
                <Card className="mx-card">
                    <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Quiz pass / fail</CardTitle></CardHeader>
                    <CardContent className="h-72">
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie data={data.quiz_donut} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                                    {data.quiz_donut.map((_, i) => <Cell key={i} fill={CHART[i]} />)}
                                </Pie>
                                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                <Card className="mx-card">
                    <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Certificates issued by course</CardTitle></CardHeader>
                    <CardContent className="h-64">
                        <ResponsiveContainer>
                            <BarChart data={data.completion_by_course}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                <XAxis dataKey="course" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                                <Bar dataKey="issued" fill="#726AF0" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
                <Card className="mx-card">
                    <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Submissions breakdown</CardTitle></CardHeader>
                    <CardContent className="h-64">
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie data={data.submission_donut} dataKey="value" nameKey="name" innerRadius={40} outerRadius={75}>
                                    {data.submission_donut.map((_, i) => <Cell key={i} fill={CHART[i]} />)}
                                </Pie>
                                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>

            <Card className="mx-card">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Recent activity</CardTitle></CardHeader>
                <CardContent>
                    {data.recent_activity.length === 0 ? <div className="text-sm text-muted-foreground py-6 text-center">No activity yet</div> :
                        <ul className="divide-y divide-border">
                            {data.recent_activity.map((a) => (
                                <li key={a.id} className="py-2.5 text-sm flex justify-between" data-testid={`activity-${a.id}`}>
                                    <span className="font-medium">{a.action}</span>
                                    <span className="text-muted-foreground text-xs">{new Date(a.created_at).toLocaleString()}</span>
                                </li>
                            ))}
                        </ul>
                    }
                </CardContent>
            </Card>
        </div>
    );
}
